import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  getListsByProcess,
  userOptionGetByProcess
} from '../store/slices/tenantSlices/dailyReportSlice';
import { dispositionOptionsGet } from '../store/slices/tenantSlices/settingSlice';

// User / Disposition / List filters all hang off the selected processes. Daily and Dump report
// expose the same three optional multi-selects, so the fetching lives here to keep them in sync.
const useProcessWiseFilterOptions = (processIds) => {
  const dispatch = useDispatch();
  const [userOptions, setUserOptions] = useState([]);
  const [dispositionOptions, setDispositionOptions] = useState([]);
  const [listOptions, setListOptions] = useState([]);

  // Formik hands back a fresh array on every change, so depend on the serialized ids instead
  const processKey = processIds?.length > 0 ? processIds.join(',') : '';

  useEffect(() => {
    if (!processKey) {
      setUserOptions([]);
      setDispositionOptions([]);
      setListOptions([]);
      return;
    }

    const ids = processKey.split(',');

    const getUserOptions = async () => {
      try {
        const response = await dispatch(
          userOptionGetByProcess({ processIds: `[${ids.join(',')}]` })
        ).unwrap();
        if (response && response.data) {
          setUserOptions(
            response.data.map((val) => ({
              label: val.Username || '-',
              value: val.id
            }))
          );
        } else {
          setUserOptions([]);
        }
      } catch (error) {
        setUserOptions([]);
        console.log('error', error);
      }
    };

    // /disposition accepts a single process, so collect and de-duplicate across the selected ones
    const getDispositionOptions = async () => {
      try {
        const responses = await Promise.all(
          ids.map((process_id) => dispatch(dispositionOptionsGet({ process_id })).unwrap())
        );
        const uniqueDispositions = [
          ...new Set(
            responses
              .flatMap((response) => response?.data || [])
              .map((val) => val?.DisposName)
              .filter(Boolean)
          )
        ];
        setDispositionOptions(uniqueDispositions.map((name) => ({ label: name, value: name })));
      } catch (error) {
        setDispositionOptions([]);
        console.log('error', error);
      }
    };

    // /lists/by-process is also single process, and a list can only belong to one process
    const getListOptions = async () => {
      try {
        const responses = await Promise.all(
          ids.map((process_id) => dispatch(getListsByProcess({ process_id })).unwrap())
        );
        const lists = new Map();
        responses
          .flatMap((response) => response?.data || [])
          .forEach((val) => {
            if (val?.id) {
              lists.set(val.id, { label: val.list_name || '-', value: val.id });
            }
          });
        setListOptions([...lists.values()]);
      } catch (error) {
        setListOptions([]);
        console.log('error', error);
      }
    };

    getUserOptions();
    getDispositionOptions();
    getListOptions();
  }, [processKey]);

  return { userOptions, dispositionOptions, listOptions };
};

export default useProcessWiseFilterOptions;
